/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { externalKeysCreateAwsKms } from "../funcs/externalKeysCreateAwsKms.js";
import { externalKeysCreateGcpKms } from "../funcs/externalKeysCreateGcpKms.js";
import { externalKeysDeleteAwsKms } from "../funcs/externalKeysDeleteAwsKms.js";
import { externalKeysDeleteGcpKms } from "../funcs/externalKeysDeleteGcpKms.js";
import { externalKeysGetAwsKms } from "../funcs/externalKeysGetAwsKms.js";
import { externalKeysGetGcpKms } from "../funcs/externalKeysGetGcpKms.js";
import { externalKeysList } from "../funcs/externalKeysList.js";
import { externalKeysListAwsKms } from "../funcs/externalKeysListAwsKms.js";
import { externalKeysListGcpKms } from "../funcs/externalKeysListGcpKms.js";
import { externalKeysUpdateAwsKms } from "../funcs/externalKeysUpdateAwsKms.js";
import { externalKeysUpdateGcpKms } from "../funcs/externalKeysUpdateGcpKms.js";
import { externalKeysVerifyGcpKms } from "../funcs/externalKeysVerifyGcpKms.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { AwsKmsKey } from "../models/components/awskmskey.js";
import { GcpKmsKey } from "../models/components/gcpkmskey.js";
import { ListExternalKeysResult } from "../models/components/listexternalkeysresult.js";
import { VerifyKmsKeyResult } from "../models/components/verifykmskeyresult.js";
import {
  CreateAwsKmsKeyRequest,
  CreateAwsKmsKeySecurity,
} from "../models/operations/createawskmskey.js";
import {
  CreateGcpKmsKeyRequest,
  CreateGcpKmsKeySecurity,
} from "../models/operations/creategcpkmskey.js";
import {
  DeleteAwsKmsKeyRequest,
  DeleteAwsKmsKeySecurity,
} from "../models/operations/deleteawskmskey.js";
import {
  DeleteGcpKmsKeyRequest,
  DeleteGcpKmsKeySecurity,
} from "../models/operations/deletegcpkmskey.js";
import {
  GetAwsKmsKeyRequest,
  GetAwsKmsKeySecurity,
} from "../models/operations/getawskmskey.js";
import {
  GetGcpKmsKeyRequest,
  GetGcpKmsKeySecurity,
} from "../models/operations/getgcpkmskey.js";
import {
  ListAwsKmsKeysRequest,
  ListAwsKmsKeysSecurity,
} from "../models/operations/listawskmskeys.js";
import {
  ListExternalKeysRequest,
  ListExternalKeysSecurity,
} from "../models/operations/listexternalkeys.js";
import {
  ListGcpKmsKeysRequest,
  ListGcpKmsKeysSecurity,
} from "../models/operations/listgcpkmskeys.js";
import {
  UpdateAwsKmsKeyRequest,
  UpdateAwsKmsKeySecurity,
} from "../models/operations/updateawskmskey.js";
import {
  UpdateGcpKmsKeyRequest,
  UpdateGcpKmsKeySecurity,
} from "../models/operations/updategcpkmskey.js";
import {
  VerifyGcpKmsKeyRequest,
  VerifyGcpKmsKeySecurity,
} from "../models/operations/verifygcpkmskey.js";
import { unwrapAsync } from "../types/fp.js";

export class ExternalKeys extends ClientSDK {
  /**
   * createAwsKmsKey externalKeys
   *
   * @remarks
   * Create an AWS KMS external key. Requires org:admin.
   */
  async createAwsKms(
    request: CreateAwsKmsKeyRequest,
    security?: CreateAwsKmsKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<AwsKmsKey> {
    return unwrapAsync(externalKeysCreateAwsKms(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * createGcpKmsKey externalKeys
   *
   * @remarks
   * Create a GCP KMS external key. Requires org:admin.
   */
  async createGcpKms(
    request: CreateGcpKmsKeyRequest,
    security?: CreateGcpKmsKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<GcpKmsKey> {
    return unwrapAsync(externalKeysCreateGcpKms(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteAwsKmsKey externalKeys
   *
   * @remarks
   * Soft-delete an AWS KMS external key by ID. Requires org:admin. Refused with a conflict while any JSON Web Key Set or published JSON Web Key still references the key, since deleting it would break verification for every already-published kid.
   */
  async deleteAwsKms(
    request: DeleteAwsKmsKeyRequest,
    security?: DeleteAwsKmsKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(externalKeysDeleteAwsKms(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteGcpKmsKey externalKeys
   *
   * @remarks
   * Soft-delete a GCP KMS external key by ID. Requires org:admin. Refused with a conflict while any JSON Web Key Set or published JSON Web Key still references the key, since deleting it would break verification for every already-published kid.
   */
  async deleteGcpKms(
    request: DeleteGcpKmsKeyRequest,
    security?: DeleteGcpKmsKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(externalKeysDeleteGcpKms(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getAwsKmsKey externalKeys
   *
   * @remarks
   * Get an AWS KMS external key by ID. Requires org:read.
   */
  async getAwsKms(
    request: GetAwsKmsKeyRequest,
    security?: GetAwsKmsKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<AwsKmsKey> {
    return unwrapAsync(externalKeysGetAwsKms(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getGcpKmsKey externalKeys
   *
   * @remarks
   * Get a GCP KMS external key by ID. Requires org:read.
   */
  async getGcpKms(
    request: GetGcpKmsKeyRequest,
    security?: GetGcpKmsKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<GcpKmsKey> {
    return unwrapAsync(externalKeysGetGcpKms(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listExternalKeys externalKeys
   *
   * @remarks
   * List the organization's external keys (provider-independent summary). Optionally filter by provider. Requires org:read.
   */
  async list(
    request?: ListExternalKeysRequest | undefined,
    security?: ListExternalKeysSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListExternalKeysResult> {
    return unwrapAsync(externalKeysList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listAwsKmsKeys externalKeys
   *
   * @remarks
   * List the organization's AWS KMS external keys. Requires org:read.
   */
  async listAwsKms(
    request?: ListAwsKmsKeysRequest | undefined,
    security?: ListAwsKmsKeysSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListExternalKeysResult> {
    return unwrapAsync(externalKeysListAwsKms(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listGcpKmsKeys externalKeys
   *
   * @remarks
   * List the organization's GCP KMS external keys. Requires org:read.
   */
  async listGcpKms(
    request?: ListGcpKmsKeysRequest | undefined,
    security?: ListGcpKmsKeysSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListExternalKeysResult> {
    return unwrapAsync(externalKeysListGcpKms(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateAwsKmsKey externalKeys
   *
   * @remarks
   * Update an AWS KMS external key's name, backing credential and customer grant reference. Requires org:admin. These three fields are replaced, not patched: omitting the optional customer_grant_reference clears it. The key ARN and algorithm are immutable: an external key identifies exactly one signable key permanently, so changing what the key is means deleting it and creating a new one. The backing credential stays editable because repairing the path to a key does not change the key material Speakeasy signs with.
   */
  async updateAwsKms(
    request: UpdateAwsKmsKeyRequest,
    security?: UpdateAwsKmsKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<AwsKmsKey> {
    return unwrapAsync(externalKeysUpdateAwsKms(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateGcpKmsKey externalKeys
   *
   * @remarks
   * Update a GCP KMS external key's name, backing credential and customer grant reference. Requires org:admin. These three fields are replaced, not patched: omitting the optional customer_grant_reference clears it. The resource name and algorithm are immutable: an external key identifies exactly one signable crypto key version permanently, so changing what the key is means deleting it and creating a new one. The backing credential stays editable because repairing the path to a key does not change the key material Speakeasy signs with.
   */
  async updateGcpKms(
    request: UpdateGcpKmsKeyRequest,
    security?: UpdateGcpKmsKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<GcpKmsKey> {
    return unwrapAsync(externalKeysUpdateGcpKms(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * verifyGcpKmsKey externalKeys
   *
   * @remarks
   * Probe that Speakeasy can reach a GCP KMS external key through its backing credential and use it to sign: read the key's public half, confirm its algorithm matches the one recorded, sign a probe digest, and verify that signature locally against the public half. Performs a real signing operation, which is billed to the key's owner and lands in their Cloud Audit Log. Ephemeral: nothing is persisted. Rate limited per organization. Requires org:admin.
   */
  async verifyGcpKms(
    request: VerifyGcpKmsKeyRequest,
    security?: VerifyGcpKmsKeySecurity | undefined,
    options?: RequestOptions,
  ): Promise<VerifyKmsKeyResult> {
    return unwrapAsync(externalKeysVerifyGcpKms(
      this,
      request,
      security,
      options,
    ));
  }
}
